import * as Combobox from '@givebutter/primitives/combobox';
import { DateValue } from '@givebutter/primitives/date-picker';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faHashtag, faPercent } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateOperatorOptions } from 'components/filter/DateOperatorOptions';
import { EmptyOperatorOptions } from 'components/filter/EmptyOperatorOptions';
import { NumberOperatorOptions } from 'components/filter/NumberOperatorOptions';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { CustomFieldFilterFormValues } from 'components/filter/filters-next/custom-field-types';
import { operatorClassName } from 'components/filter/filters-next/props';
import { validateFilterDate, zeroCustomFieldsSelected } from 'components/filter/validations';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { CustomField } from 'types/domain';

interface CustomFieldFilterProps {
    customField: CustomField;
}

const CustomFieldFilter = (props: CustomFieldFilterProps) => {
    const { customField } = props;
    const formContext = Form.useFormContext<CustomFieldFilterFormValues>();
    const { path, control, watch } = formContext;
    const filterOperator = watch('filterOperator');
    const filterOperatorIsEmptyOrIsNotEmpty = ['is_empty', 'is_not_empty'].includes(
        filterOperator ?? '',
    );

    switch (customField.type) {
        case 'checkbox':
            return (
                <Form.Container>
                    <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                    <Form.Switch name={`value.value`} control={control} />
                </Form.Container>
            );
        case 'number':
        case 'percentage':
            return (
                <Form.Container>
                    <div className={'flex items-center gap-1'}>
                        <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                        <Form.SingleSelect control={control} name="filterOperator">
                            <SingleSelect.Trigger
                                className={operatorClassName}
                                aria-label="Filter operator"
                            />
                            <SingleSelect.Content>
                                <NumberOperatorOptions />
                                <EmptyOperatorOptions />
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                    </div>
                    {!filterOperatorIsEmptyOrIsNotEmpty && (
                        <>
                            <Form.TextInputRoot
                                control={control}
                                name={`value.value`}
                                rules={{
                                    required: ValidationTextMap.Required,
                                }}
                                inputRootProps={{
                                    mask: 'decimal',
                                }}
                            >
                                <Form.TextInputForm />
                                <FontAwesomeIcon
                                    icon={customField.type === 'percentage' ? faPercent : faHashtag}
                                    className={'text-gray-600'}
                                />
                            </Form.TextInputRoot>
                            <Form.Error name={path('value.value')} />
                        </>
                    )}
                </Form.Container>
            );
        case 'date':
            return (
                <Form.Container className={'gap-3'}>
                    <div className={'flex items-center gap-1'}>
                        <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                        <Form.SingleSelect control={control} name="filterOperator">
                            <SingleSelect.Trigger
                                className={operatorClassName}
                                aria-label="Filter operator"
                            />
                            <SingleSelect.Content>
                                <DateOperatorOptions />
                                <EmptyOperatorOptions />
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                    </div>
                    {!filterOperatorIsEmptyOrIsNotEmpty && (
                        <Form.Container
                            className={
                                '[&>*]:items-center [&_.react-calendar]:w-72 [&_.react-calendar]:p-0'
                            }
                        >
                            <Form.UseFormDatePicker
                                name={'value.value'}
                                clearable={true}
                                control={control}
                                selectRange={filterOperator === 'bet'}
                                isTextInput={false}
                                rules={{
                                    validate: value => validateFilterDate(value as DateValue),
                                    required: ValidationTextMap.Required,
                                }}
                            />
                            <Form.Error name={path('value')} />
                        </Form.Container>
                    )}
                </Form.Container>
            );
        case 'daterange':
            return (
                <Form.Container className={'gap-3'}>
                    <div className={'flex items-center gap-1'}>
                        <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                        <Form.SingleSelect control={control} name="filterOperator">
                            <SingleSelect.Trigger
                                className={operatorClassName}
                                aria-label="Filter operator"
                            />
                            <SingleSelect.Content>
                                <SingleSelect.Item value="includes">
                                    <SingleSelect.ItemText>Includes</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="excludes">
                                    <SingleSelect.ItemText>Excludes</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <EmptyOperatorOptions />
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                    </div>
                    {!filterOperatorIsEmptyOrIsNotEmpty && (
                        <Form.Container
                            className={
                                '[&>*]:items-center [&_.react-calendar]:w-72 [&_.react-calendar]:p-0'
                            }
                        >
                            <Form.UseFormDatePicker
                                control={control}
                                name={`value.value`}
                                selectRange={true}
                                rules={{
                                    validate: value => validateFilterDate(value as DateValue),
                                }}
                            />
                            <Form.Error name={path('value.value')} />
                        </Form.Container>
                    )}
                </Form.Container>
            );
        case 'textarea':
        case 'text':
            return (
                <Form.Container className={'gap-3'}>
                    <div className={'flex items-center gap-1'}>
                        <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                        <Form.SingleSelect control={control} name="filterOperator">
                            <SingleSelect.Trigger
                                className={operatorClassName}
                                aria-label="Filter operator"
                            />
                            <SingleSelect.Content>
                                <SingleSelect.Item value="includes">
                                    <SingleSelect.ItemText>Includes</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="excludes">
                                    <SingleSelect.ItemText>Excludes</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <EmptyOperatorOptions />
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                    </div>
                    {!filterOperatorIsEmptyOrIsNotEmpty && (
                        <>
                            <Form.TextInputRoot
                                control={control}
                                name={`value.value`}
                                rules={{
                                    required: ValidationTextMap.Required,
                                }}
                            >
                                <Form.TextInputForm />
                            </Form.TextInputRoot>
                            <Form.Error name={path('value.value')} />
                        </>
                    )}
                </Form.Container>
            );
        case 'select':
        case 'multiselect':
            return (
                <Form.Container className={'gap-3'}>
                    <div className={'flex items-center gap-1'}>
                        <Form.Label name={path('value.value')}>{customField.title}</Form.Label>
                        {customField.type === 'multiselect' && (
                            <Form.SingleSelect control={control} name="filterOperator">
                                <SingleSelect.Trigger
                                    className={operatorClassName}
                                    aria-label="Filter operator"
                                />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="all">
                                        <SingleSelect.ItemText>All</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="any">
                                        <SingleSelect.ItemText>Any</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="none">
                                        <SingleSelect.ItemText>None</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        )}
                    </div>
                    <Form.Combobox
                        resource="value"
                        name="value.value"
                        control={control}
                        rules={{ validate: value => zeroCustomFieldsSelected(value as string[]) }}
                    >
                        <Combobox.Content>
                            <Combobox.Search />
                            <FilterComboboxItems>
                                {customField.answers?.map(answer => (
                                    <Combobox.Item value={answer} key={answer}>
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>{answer}</Combobox.ItemText>
                                    </Combobox.Item>
                                ))}
                            </FilterComboboxItems>
                        </Combobox.Content>
                    </Form.Combobox>
                    <Form.Error name={path('value.value')} />
                </Form.Container>
            );
        default:
            return null;
    }
};

export { CustomFieldFilter };
